import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, MessageSquare, CalendarCheck, ShieldCheck, Train, Briefcase, GraduationCap,
  HeartPulse, ShoppingBag, Building2, BadgeCheck, MapPin, Ruler, LayoutGrid, Wind,
  Sparkles, Trees, Award, IndianRupee, ClipboardList,
} from "lucide-react";
import { initLenis } from "../lib/smooth";
import { getSiteSettings, digitsOnly } from "../lib/api";
import { Header } from "../components/Header";
import { Footer } from "../components/ContactFooter";
import { LeadModal } from "../components/LeadForm";
import { StickyMobileBar, WhatsAppFloat } from "../components/StickyElements";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Reveal, SectionHeading } from "../components/Reveal";
import { IMAGES, AGENT_REG, WA_MESSAGE } from "../data/site";

const AREA_ADVANTAGES = [
  { icon: Train, title: "Connectivity", desc: "The nearest metro is indicatively about 12 minutes from the Hatishala edge of the Action Area III vicinity, with the airport around 30 minutes away." },
  { icon: Briefcase, title: "Employment & IT Hubs", desc: "Major workplaces sit close by — Infosys about 1 min, ITC Infotech and Wipro about 2 mins, and TCS about 12 mins (indicative travel times)." },
  { icon: GraduationCap, title: "Education", desc: "Institutions such as Orchids The International School (~7 mins), DPS Newtown (~15 mins), St Xavier's University (~7 mins) and Sister Nivedita University (~15 mins) serve the area." },
  { icon: HeartPulse, title: "Healthcare", desc: "Wellcare Nursing Home is under a minute away, with Tata Medical Centre (~12 mins), Neotia Bhagirathi (~15 mins) and Ohio Hospital (~15 mins) nearby." },
  { icon: ShoppingBag, title: "Everyday Conveniences", desc: "Daily needs stay close: Gabtala Bazaar (~1 min), Spencer's (~6 mins), Downtown Mall and Rosedale Plaza (~7 mins each)." },
  { icon: Building2, title: "Residential Development", desc: "The corridor continues to attract planned residential projects, making it one of New Town's most active housing micro-markets." },
];

const CHECKLIST = [
  { icon: BadgeCheck, text: "RERA registration of the project and the agent — verify on the WBRERA website" },
  { icon: MapPin, text: "Location and real travel times at the hours you commute" },
  { icon: Ruler, text: "Carpet area, not just super built-up area" },
  { icon: LayoutGrid, text: "Floor plan efficiency and usable room sizes" },
  { icon: Wind, text: "Ventilation and natural light — open sides matter" },
  { icon: Sparkles, text: "Amenities your family will genuinely use" },
  { icon: Trees, text: "Open and green spaces within the project" },
  { icon: Train, text: "Connectivity to metro, workplaces and daily needs" },
  { icon: Award, text: "Developer track record of delivery" },
  { icon: IndianRupee, text: "Full pricing including applicable additional charges" },
  { icon: ClipboardList, text: "Current inventory — which towers, floors and types are actually available" },
];

const WHY_21_ACRES = [
  "2 & 3 BHK residences across five towers",
  "90% homes with 3-side open views",
  "Double-height sky terraces",
  "Efficient floor plans with reduced circulation space",
  "Vastu-compliant layouts",
  "Planned around natural light and cross ventilation",
  "IGBC Precertified Platinum Rated Green Building",
  "3-acre central park with gardens, courts and trails",
  "Shuttle service to the nearest metro planned",
];

const AA3_FAQS = [
  {
    q: "What types of properties are available around New Town Action Area 3?",
    a: "The Action Area III vicinity is primarily a residential micro-market with apartments in various configurations. Buyers will mostly compare 2 BHK and 3 BHK flats in gated projects — for example, Vinayak 21 Acres at Hatishala, adjacent to Action Area III, offers 2 BHK 2T homes and 3 BHK Smart, Comfort and Luxury configurations.",
  },
  {
    q: "Are 2 BHK flats available near Action Area 3?",
    a: "Yes. Vinayak 21 Acres offers 2 BHK 2T residences of approximately 595–597 sq ft carpet area with about 60 sq ft balcony, across Towers 1A, 1B, 2A and 2B.",
  },
  {
    q: "Are 3 BHK flats available near Action Area 3?",
    a: "Yes. The 3 BHK options at Vinayak 21 Acres come in three families — Smart (approx. 698–703 sq ft carpet), Comfort (approx. 736–783 sq ft) and Luxury (approx. 823–943 sq ft) — across five towers.",
  },
  {
    q: "What should I check before buying property in New Town?",
    a: "Verify RERA registration, compare carpet areas and floor plans rather than just SBUA, check ventilation and open sides, review amenities and open spaces, test real connectivity at your commute hours, research the developer's track record, and ask for complete pricing with applicable charges plus current inventory.",
  },
  {
    q: "Is Vinayak 21 Acres inside Action Area 3?",
    a: "No. According to the available project information, Vinayak 21 Acres is located at Hatishala — off and adjacent to Newtown Action Area III — not physically inside Action Area III itself.",
  },
  {
    q: "Where exactly is Vinayak 21 Acres located?",
    a: "The project is at Hatishala, Kolkata, adjacent to Newtown Action Area III — close to the IT hub, schools, hospitals and daily conveniences of New Town.",
  },
  {
    q: "How can I check current prices?",
    a: "Prices and availability are subject to change. Contact Forest Hill Realtors through the enquiry buttons on this page, WhatsApp or a call for the latest developer-provided pricing and inventory.",
  },
  {
    q: "How can I book a site visit?",
    a: "Use any Book a Site Visit button on this page, the sticky enquiry bar on mobile, or message us on WhatsApp — Forest Hill Realtors will arrange a convenient slot.",
  },
];

export default function ActionArea3() {
  const [modal, setModal] = useState({ open: false, source: "Action Area 3 Page Enquiry", interest: "" });
  const [contact, setContact] = useState({ phone: "+919163841785", whatsapp: "+919163841785" });

  useEffect(() => {
    document.title = "Properties in New Town Action Area 3 | Flats & Residential Projects";
    const meta = document.querySelector('meta[name="description"]');
    if (meta)
      meta.setAttribute(
        "content",
        "Explore properties in and around New Town Action Area 3 Kolkata. Discover residential options, location advantages and Vinayak 21 Acres near Action Area III."
      );
    initLenis();
    getSiteSettings().then(setContact);
  }, []);

  const openModal = (source, interest = "") => setModal({ open: true, source, interest });
  const waLink = `https://wa.me/${digitsOnly(contact.whatsapp)}?text=${encodeURIComponent(WA_MESSAGE)}`;

  return (
    <div className="bg-[#FFFFFF]" data-testid="aa3-page">
      <Header onBookVisit={() => openModal("Site Visit Booking")} phone={contact.phone} />
      <main>
        <section className="relative bg-[#EAF5D6] pt-36 pb-16 sm:pt-44 sm:pb-20 overflow-hidden hero-grain">
          <div className="absolute inset-0 opacity-[0.14]" aria-hidden="true">
            <img src={IMAGES.hero} alt="" className="w-full h-full object-cover" loading="eager" />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="overline-tag" data-testid="aa3-overline">Locality Guide · Forest Hill Realtors</p>
            <h1
              className="font-serif-display text-[#3F6212] text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight mt-6 max-w-4xl"
              data-testid="aa3-h1"
            >
              Properties in New Town <span className="text-[#84CC16]">Action Area 3</span>
            </h1>
            <p className="mt-6 text-[#4D7C0F] text-base sm:text-lg max-w-2xl leading-relaxed">
              A practical buyer's guide to New Town Action Area 3 properties — what the vicinity offers, what to
              compare before you book, and a look at residential projects in Action Area 3's immediate surroundings,
              including Vinayak 21 Acres at nearby Hatishala.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/" data-testid="aa3-home-link"
                className="inline-flex items-center gap-2 text-[#84CC16] font-sub font-semibold text-sm border border-[#84CC16]/50 rounded-full px-6 py-3 hover:bg-[#84CC16]/10 active:scale-[0.98] transition-all duration-300">
                Explore Vinayak 21 Acres <ArrowRight size={15} />
              </Link>
              <Link to="/best-property-in-new-town-kolkata" data-testid="aa3-guide-link"
                className="inline-flex items-center gap-2 text-[#3F6212] font-sub font-semibold text-sm border border-[#3F6212]/30 rounded-full px-6 py-3 hover:bg-[#3F6212]/10 active:scale-[0.98] transition-all duration-300">
                Best Property in New Town Kolkata Guide <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </section>

        <section data-testid="aa3-buyer-guide-section" className="py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="01" eyebrow="Start Here" title="Properties in New Town Action Area 3 – Buyer Guide" />
            <Reveal delay={0.15}>
              <div className="mt-6 space-y-4 text-[#4D7C0F] text-base sm:text-lg leading-relaxed">
                <p>
                  Buyers searching for property in New Town Action Area 3 are usually drawn by the same combination:
                  a planned urban zone, serious employment nearby, and housing that still leaves room to breathe.
                  When comparing flats in New Town Action Area 3 and its surroundings, start with your daily map —
                  workplace, school, hospital and market — then layer in the quality of the project itself.
                </p>
                <p>
                  This page summarises the locality's verified advantages, a checklist of what to check before buying,
                  and one residential option adjacent to the area worth shortlisting.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="aa3-advantages-section" className="py-16 sm:py-24 bg-[#F4FAE8]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="02" eyebrow="The Locality" title="Why Buyers Consider the Action Area III Vicinity" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
              {AREA_ADVANTAGES.map((a, i) => (
                <Reveal key={a.title} delay={0.04 * i}>
                  <div data-testid={`aa3-advantage-${i}`}
                    className="bg-white border border-[#DCECC0] rounded-xl p-6 h-full hover:border-[#84CC16] hover:-translate-y-1 hover:shadow-[0_20px_40px_-18px_rgba(77,124,15,0.18)] transition-all duration-500">
                    <div className="w-11 h-11 rounded-full bg-[#EAF5D6] border border-[#84CC16]/30 flex items-center justify-center text-[#365314]">
                      <a.icon size={19} strokeWidth={1.6} />
                    </div>
                    <h3 className="font-serif-display text-xl text-[#365314] mt-4 leading-snug">{a.title}</h3>
                    <p className="text-sm text-[#65A30D] mt-2 leading-relaxed">{a.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.2}>
              <p className="text-xs text-[#65A30D] mt-8">Travel times are indicative and may vary depending on traffic and route.</p>
            </Reveal>
          </div>
        </section>

        <section data-testid="aa3-types-section" className="py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="03" eyebrow="Configurations" title="Types of Residential Properties" />
            <Reveal delay={0.15}>
              <div className="mt-6 space-y-4 text-[#4D7C0F] text-base sm:text-lg leading-relaxed">
                <p>
                  Most residential projects in Action Area 3's surroundings centre on 2 BHK and 3 BHK apartments. A
                  2 BHK suits young families and first-time buyers; 3 BHK homes range from compact, efficient layouts
                  to large luxury formats with extra toilets and terrace decks.
                </p>
                <p>
                  When comparing options, look past the headline size: compare carpet area, balcony provision, the
                  number of toilets, open views and how much of the plan is actually usable room versus passage.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="aa3-checklist-section" className="py-16 sm:py-24 bg-[#EAF5D6]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="04" eyebrow="Due Diligence" title="What to Check Before Buying" dark />
            <div className="grid sm:grid-cols-2 gap-3.5 mt-12 max-w-5xl">
              {CHECKLIST.map((c, i) => (
                <Reveal key={c.text} delay={0.04 * i}>
                  <div data-testid={`aa3-check-${i}`} className="flex items-start gap-4 border border-[#3F6212]/12 rounded-xl p-5 bg-[#3F6212]/[0.04]">
                    <c.icon size={18} className="text-[#84CC16] shrink-0 mt-0.5" strokeWidth={1.6} />
                    <p className="text-sm text-[#3F6212] leading-relaxed">{c.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section data-testid="aa3-project-section" className="py-16 sm:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="05" eyebrow="One Option Nearby" title="Vinayak 21 Acres Near Newtown Action Area III" />
            <Reveal delay={0.15}>
              <p className="mt-6 text-[#4D7C0F] text-base sm:text-lg leading-relaxed max-w-3xl">
                Among the residential choices around the Action Area III vicinity is Vinayak 21 Acres, located at
                Hatishala — off and adjacent to Newtown Action Area III, rather than inside it. Based on the project's
                published information, its verified features include:
              </p>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3.5 mt-8 max-w-5xl">
              {WHY_21_ACRES.map((f, i) => (
                <Reveal key={f} delay={0.03 * i}>
                  <div data-testid={`aa3-why-item-${i}`} className="flex items-start gap-3 bg-white border border-[#DCECC0] rounded-xl px-5 py-4">
                    <span className="text-[#84CC16] mt-0.5">✦</span>
                    <p className="text-sm text-[#365314] font-sub font-medium leading-relaxed">{f}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section data-testid="aa3-options-section" className="py-16 sm:py-24 bg-[#F4FAE8]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="06" eyebrow="Configurations" title="2 BHK & 3 BHK Options" />
            <div className="grid md:grid-cols-2 gap-6 mt-10">
              <Reveal>
                <div className="bg-white border border-[#DCECC0] rounded-2xl p-8 h-full" data-testid="aa3-2bhk-card">
                  <h3 className="font-serif-display text-2xl text-[#365314]">2 BHK 2T</h3>
                  <p className="text-sm text-[#4D7C0F] mt-3 leading-relaxed">
                    Approx. 595–597 sq ft carpet area with about 60 sq ft balcony, 728 sq ft BUA and 971 sq ft SBUA —
                    available as Type B and Type G across Towers 1A, 1B, 2A and 2B.
                  </p>
                  <button data-testid="aa3-check-2bhk-btn" onClick={() => openModal("Availability Enquiry: 2 BHK", "2 BHK")}
                    className="mt-6 bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] font-sub font-bold text-sm px-7 py-3.5 rounded-full hover:brightness-105 active:scale-[0.98] transition-all duration-300">
                    Check Availability
                  </button>
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="bg-white border border-[#DCECC0] rounded-2xl p-8 h-full" data-testid="aa3-3bhk-card">
                  <h3 className="font-serif-display text-2xl text-[#365314]">3 BHK — Smart, Comfort & Luxury</h3>
                  <p className="text-sm text-[#4D7C0F] mt-3 leading-relaxed">
                    From approx. 698 sq ft carpet (Smart) to about 943 sq ft carpet (Luxury) — 2-toilet and 3-toilet
                    layouts across five towers, with balcony, BUA and SBUA varying by odd/even sky-terrace floors.
                  </p>
                  <button data-testid="aa3-check-3bhk-btn" onClick={() => openModal("Availability Enquiry: 3 BHK", "3 BHK")}
                    className="mt-6 bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] font-sub font-bold text-sm px-7 py-3.5 rounded-full hover:brightness-105 active:scale-[0.98] transition-all duration-300">
                    Check Availability
                  </button>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        <section data-testid="aa3-price-section" className="py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="07" eyebrow="Pricing" title="Price & Availability" />
            <Reveal delay={0.15}>
              <div className="bg-white border border-[#DCECC0] rounded-2xl p-7 sm:p-10 mt-8 shadow-[0_24px_48px_-24px_rgba(77,124,15,0.18)]">
                <p className="text-[#4D7C0F] text-base sm:text-lg leading-relaxed">
                  Pricing varies by configuration, tower, floor, unit selection, applicable charges and current
                  availability — and both change over time. For current developer-provided pricing and inventory,
                  contact Forest Hill Realtors directly.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <button data-testid="aa3-get-price-btn" onClick={() => openModal("Get Latest Price")}
                    className="bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] font-sub font-bold text-sm px-7 py-3.5 rounded-full hover:brightness-105 active:scale-[0.98] transition-all duration-300">
                    Get Latest Price
                  </button>
                  <Link to="/vinayak-21-acres-price" data-testid="aa3-price-link"
                    className="inline-flex items-center gap-2 text-[#365314] font-sub font-semibold text-sm border border-[#9CCB58] rounded-full px-6 py-3 hover:bg-[#EAF5D6] hover:text-[#3F6212] active:scale-[0.98] transition-all duration-300">
                    Vinayak 21 Acres Price <ArrowRight size={15} />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="aa3-floorplans-section" className="py-16 sm:py-24 bg-[#F4FAE8]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="08" eyebrow="Layouts" title="Floor Plans" />
            <Reveal delay={0.15}>
              <p className="mt-6 text-[#4D7C0F] text-base sm:text-lg leading-relaxed">
                Every official floor plan — all 40 unit layouts across Towers 1A, 1B, 2A, 2B and 2C — is available to
                view with its carpet area, balcony, BUA and SBUA on our dedicated floor plans page.
              </p>
              <div className="mt-6">
                <Link to="/vinayak-21-acres-floor-plan" data-testid="aa3-floorplan-link"
                  className="inline-flex items-center gap-2 text-[#365314] font-sub font-semibold text-sm border border-[#9CCB58] rounded-full px-6 py-3 hover:bg-[#EAF5D6] hover:text-[#3F6212] active:scale-[0.98] transition-all duration-300">
                  Vinayak 21 Acres Floor Plans <ArrowRight size={15} />
                </Link>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="aa3-visit-section" className="py-16 sm:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="relative overflow-hidden rounded-2xl bg-[#EAF5D6] p-8 sm:p-12 text-center hero-grain">
                <p className="overline-tag">09 · Visit</p>
                <h2 className="font-serif-display text-3xl sm:text-4xl text-[#3F6212] leading-tight mt-4">
                  Book a Site Visit
                </h2>
                <p className="text-[#4D7C0F] text-sm sm:text-base mt-4 max-w-xl mx-auto leading-relaxed">
                  See the Action Area III vicinity and the project in person — the commute, the light, the ventilation
                  and the 3-acre central park.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center max-w-2xl mx-auto">
                  <button data-testid="aa3-book-visit-btn" onClick={() => openModal("Site Visit Booking")}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] font-sub font-bold text-sm px-7 py-4 rounded-full hover:brightness-105 active:scale-[0.98] transition-all duration-300">
                    <CalendarCheck size={16} /> Book a Site Visit
                  </button>
                  <a data-testid="aa3-whatsapp-btn" href={waLink} target="_blank" rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-sub font-bold text-sm px-7 py-4 rounded-full hover:bg-[#1EBE57] active:scale-[0.98] transition-all duration-300">
                    <MessageSquare size={16} /> WhatsApp Us
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="aa3-faq-section" className="pb-16 sm:pb-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="10" eyebrow="FAQs" title="Action Area 3 Property FAQs" center />
            <Reveal delay={0.15}>
              <Accordion type="single" collapsible className="mt-10 space-y-3" data-testid="aa3-faq-accordion">
                {AA3_FAQS.map((f, i) => (
                  <AccordionItem key={f.q} value={f.q} data-testid={`aa3-faq-item-${i}`}
                    className="bg-white border border-[#DCECC0] rounded-xl px-6 data-[state=open]:border-[#84CC16] transition-colors duration-300">
                    <AccordionTrigger data-testid={`aa3-faq-trigger-${i}`}
                      className="text-left font-sub font-medium text-[15px] text-[#365314] hover:no-underline py-5">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="pb-5 text-sm text-[#4D7C0F] leading-relaxed">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </section>

        <div className="bg-[#EAF5D6] border-t border-[#DCECC0] py-7" data-testid="aa3-partner-strip">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
            <ShieldCheck size={17} className="text-[#84CC16] shrink-0" />
            <p className="text-[#4D7C0F] text-xs sm:text-[13px] leading-relaxed">
              <span className="text-[#3F6212] font-sub font-semibold">Forest Hill Realtors</span> · Authorised Channel
              Partner · WBRERA Agent Registration: <span className="font-mono-spec">{AGENT_REG}</span>
            </p>
          </div>
        </div>

        <Footer />
      </main>
      <LeadModal open={modal.open} onClose={() => setModal((m) => ({ ...m, open: false }))} source={modal.source} defaultInterest={modal.interest} whatsapp={contact.whatsapp} />
      <StickyMobileBar phone={contact.phone} whatsapp={contact.whatsapp} onEnquire={() => openModal("Quick Enquiry")} />
      <WhatsAppFloat whatsapp={contact.whatsapp} />
    </div>
  );
}
